<template>
  <div class="boost-active-modal">
    <div class="overlay" @click="$emit('close')"></div>
    <transition name="pop-in" appear>
      <div
        class="card"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('boost_active_badge')"
      >
        <div class="hero">
          <div class="ring" :style="{ '--progress': progressPercent + '%' }">
            <span class="ring-sweep" aria-hidden="true"></span>
            <div class="ring-inner">
              <div class="multiplier">{{ BOOST_EXPOSURE_MULTIPLIER }}×</div>
              <div class="multiplier-label">
                {{ $t('boost_active_multiplier_label') }}
              </div>
            </div>
          </div>

          <div class="badge">
            <span class="badge-dot"></span>
            {{ $t('boost_active_badge') }}
          </div>

          <h1 v-if="timeLeft" class="time-left">{{ timeLeftText }}</h1>
          <p class="text">{{ $t('boost_active_text') }}</p>
        </div>

        <button
          ref="closeButton"
          type="button"
          class="close-button"
          @click="$emit('close')"
        >
          {{ $t('boost_active_close') }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { lbApiBeta } from '@/utils/axiosConfig';
import {
  remainingDaysHours,
  startTimeTicker,
  timeTicker
} from '@/utils/helpers/relativeTime';
import { BOOST_EXPOSURE_MULTIPLIER } from '@/utils/constants/boost';

const SCROLL_LOCK_NAME = 'BoostActiveModal';

// Consumables variant of the "Din Boost" modal while the boost runs:
// the compact status card (design 5a) — elapsed ring, countdown, Okej.
// A light sweeps around the ring for as long as the card is open, so the
// card reads as live rather than as a still picture of a finished boost.
// The countdown runs on endTime, the instant the boost ends — the ad's
// own end timestamp, passed in by the container; the ring's start comes
// from /api/boost-summary (activeFrom), the only place the backend
// reports it.
export default {
  name: 'BoostActiveModal',

  props: {
    // When the boost ends, in ms.
    endTime: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      BOOST_EXPOSURE_MULTIPLIER,
      activeFrom: null,
      lastFocusedElement: null
    };
  },

  computed: {
    ...mapGetters({
      primaryProposition: 'myPropositions/primaryProposition'
    }),

    timeLeft() {
      return remainingDaysHours(this.endTime, timeTicker.now);
    },

    // The last hour ticks down in minutes; above that in hours, and in
    // days and hours once more than a day remains. A whole number of days
    // drops the "0 tim" part.
    timeLeftText() {
      const { days, hours, minutes } = this.timeLeft;
      if (minutes > 0) {
        return this.$tc('boost_active_time_left_minutes', minutes, { minutes });
      }
      if (days === 0) {
        return this.$tc('boost_active_time_left_hours', hours, { hours });
      }
      if (hours === 0) {
        return this.$tc('boost_active_time_left_days', days, { days });
      }
      return this.$tc('boost_active_time_left', days, { days, hours });
    },

    // Share of the boost that has elapsed, 0–100 — the white part of the
    // ring. Empty until the summary has reported the start.
    progressPercent() {
      if (!this.activeFrom) return 0;
      const start = new Date(this.activeFrom).getTime();
      const total = this.endTime - start;
      if (!(total > 0)) return 100;
      const share = (timeTicker.now - start) / total;
      return Math.round(Math.min(Math.max(share, 0), 1) * 100);
    }
  },

  created() {
    startTimeTicker();
    this.lastFocusedElement = document.activeElement;
    this.addDisableScroll(SCROLL_LOCK_NAME);
    this.loadSummary();
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    if (this.lastFocusedElement && this.lastFocusedElement !== document.body) {
      this.$nextTick(() => this.$refs.closeButton?.focus());
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    this.removeDisableScroll(SCROLL_LOCK_NAME);
    this.lastFocusedElement?.focus();
  },

  methods: {
    ...mapMutations({
      addDisableScroll: 'ui/addDisableScroll',
      removeDisableScroll: 'ui/removeDisableScroll'
    }),

    async loadSummary() {
      try {
        const { data } = await lbApiBeta.get(
          `/api/boost-summary?propositionId=${this.primaryProposition?.propositionId}`
        );
        this.activeFrom = data.activeFrom || null;
      } catch (e) {
        window.Logger.error(e);
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.boost-active-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(11, 24, 44, 0.55);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.card {
  position: relative;
  width: 100%;
  max-width: 327px;
  overflow: hidden;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(11, 24, 44, 0.35);
  text-align: center;

  @media ($desktop) {
    max-width: 360px;
  }
}

.hero {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: 30px 22px 26px;
  color: #fff;
  background: linear-gradient(160deg, #b05df2 0%, #9f41e9 45%, #5b1fa3 100%);

  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -70px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }
}

.ring {
  position: relative;
  display: grid;
  place-items: center;
  width: 126px;
  height: 126px;
  border-radius: 50%;
  background: conic-gradient(
    #fff 0 var(--progress),
    rgba(255, 255, 255, 0.25) var(--progress) 100%
  );
}

// One turn of light around the ring: the boost is running right now. The
// inner disc covers the middle, so the conic sweep only shows in the band.
.ring-sweep {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    rgba(255, 255, 255, 0) 0deg,
    rgba(255, 255, 255, 0) 250deg,
    rgba(255, 255, 255, 0.75) 340deg,
    rgba(255, 255, 255, 0) 360deg
  );
  animation: boost-ring-sweep 2.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0;
  }
}

@keyframes boost-ring-sweep {
  to {
    transform: rotate(360deg);
  }
}

.ring-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: #9f41e9;
}

.multiplier {
  font-family: $font-headings;
  font-weight: 700;
  font-size: 42px;
  line-height: 1;
  letter-spacing: -0.04em;
}

.multiplier-label {
  margin-top: 4px;
  font-weight: 600;
  font-size: 11px;
  line-height: 1.2;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.3);
}

.time-left {
  position: relative;
  margin: 12px 0 0;
  font-family: $font-headings;
  font-weight: 700;
  font-size: 26px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.text {
  position: relative;
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.92;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 60px;
  border: 0;
  background: none;
  font-family: inherit;
  font-weight: 700;
  font-size: 16px;
  color: var(--Text-Primary);
  cursor: pointer;

  &:hover {
    background: #f6f7f9;
  }

  &:focus-visible {
    outline: 2px solid var(--Primary-Color);
    outline-offset: -2px;
  }
}
</style>
